import React, { useEffect } from 'react';
import styled from 'styled-components';
import { formatCents } from '@src/common';
import {
  extractLimitFrom,
  getTariffPriceForScheduleTime,
  scheduleIsAvailable,
  tariffIsAvailable,
  getSelectedTariff,
  tariffDepedencyStatus,
  getCorrectTariffFromScheduling,
  splitHourList,
  ITariffPrice,
  tariffPriceWithDiscount,
} from '@src/common/tariffs';
import { schedulingContainsMinute } from '@src/common/schedulings';
import { useAppDispatch, useAppSelector, useTranslate } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import { Scheduling, ScheduleTime, SellingMode } from '@src/core/buyerApiClient';
import {
  changeSelectedHour,
  productPackageProductDiscountSelector,
  productSelector,
  selectedProductTariffHour,
  TariffsPerType,
  tariffsState,
  updateProductAvailableDateTariff,
} from '@src/features/product';
import {
  BodyRegular,
  BodyRegularBold,
  BodyRegularMedium,
  BodyRegularSemiBold,
  BodySmall,
  BodySmallMedium,
  BodySmallSummary,
} from '@src/fonts/typography/Typography';
import { DarkTextColor } from '@src/fonts/typography/TypographyColors';
import BasicQuantitySelectorInput from '@src/components/reusable/input/quantitySelector/BasicQuantitySelectorInput';
import ProductAdderFooterButtons from './ProductAdderFooterButtons';
import ProductAdderOngoingCartResume from './ProductAdderSummary';
import { track } from '@src/services/analytics';
import TimeSelector from '@src/components/reusable/TimeSelector';
import { getSchedulingFrom } from '@src/common/seatMaps';
import SeatsUnavailableAlert from './SeatsUnavailableAlert';
import useSelectedProductId from '@src/hooks/useSelectedProductId';
import usePackageIdParam from '@src/hooks/usePackageIdParam';

const ProductAdderTariff = () => {
  const dispatch = useAppDispatch();

  const widgetId = useWidgetId();
  const prodId = useSelectedProductId();
  const { selectedDate } = useAppSelector(tariffsState(widgetId));
  const tariffsInformation = useAppSelector(tariffsState(widgetId));
  const { listOfTariffsPerType } = tariffsInformation;
  const selectedHour = useAppSelector(selectedProductTariffHour(widgetId));
  const product = useAppSelector(productSelector(prodId))!;
  const { i18n } = useTranslate();
  const packageId = usePackageIdParam();
  const tariffPackageDiscount = useAppSelector(productPackageProductDiscountSelector(widgetId, prodId, packageId));

  const selectedTariffsPerType = listOfTariffsPerType.filter((t) => t.selectedQuantity);
  const selectedTime = selectedHour?.minutes;
  const scheduling = getSchedulingFrom(product, selectedTariffsPerType, selectedTime);

  // Obtain the list of all the distinct schedule times
  const scheduleTimes: ScheduleTime[] = Object.values(
    selectedTariffsPerType.reduce(
      (carry, tariffsPerType) =>
        tariffsPerType.schedulings.reduce(
          (c2, sch) => (sch.availableScheduleTimes ?? []).reduce((c3, st) => ({ ...c3, [st.formattedTime]: st }), c2),
          carry,
        ),
      {},
    ),
  );

  // Check which schedule times are available
  const hoursList = scheduleTimes.map((hour) => ({
    hour,
    error: !scheduleIsAvailable(tariffsInformation, hour.minutes),
  }));

  // Filter to display only the tariff types that are either selected or which are available at the same time as all the other tariff types
  const tariffsExhibitedList = listOfTariffsPerType.filter(
    ({ selectedQuantity, schedulings }) =>
      selectedQuantity ||
      hoursList.length === 0 ||
      selectedTariffsPerType.every((tt) =>
        tt.schedulings.some((sch) =>
          sch.availableScheduleTimes?.some((st) =>
            schedulings.some((otherSch) => schedulingContainsMinute(otherSch, st.minutes)),
          ),
        ),
      ),
  );

  // Sort tariffs based on whether they are available at all
  const tariffsExhibitedListSorted: TariffsPerTypeExtended[] = tariffsExhibitedList
    .map((tariffPerType) => ({
      ...tariffPerType,
      isAvailable: tariffIsAvailable(product, tariffPerType),
    }))
    .sort((a, b) => (a.isAvailable === b.isAvailable ? 0 : a.isAvailable ? -1 : 1));

  useEffect(() => {
    const hoursWithErrors = hoursList?.filter((value) => value.error) || [];
    const selectedHourHasError = hoursWithErrors.some(
      (value) => value.hour.formattedTime === selectedHour?.formattedTime,
    );

    if (selectedHourHasError) {
      dispatch(changeSelectedHour({ id: widgetId, value: undefined }));
    }

    if (hoursList.length === 1 && !hoursList[0]!.error) {
      dispatch(changeSelectedHour({ id: widgetId, value: hoursList[0]!.hour }));
    }
  }, [dispatch, hoursList, widgetId, selectedHour]);

  const acceptableQuantities = (tariffsPerType: TariffsPerType): [number, number] => {
    const range = extractLimitFrom(tariffsInformation, tariffsPerType, selectedHour?.minutes);
    const saleTariffQuantities: [number, number] = [product.minSaleTariffQuantity, product.maxSaleTariffQuantity];

    if (range && saleTariffQuantities) {
      const min = saleTariffQuantities[0];
      const max = Math.min(saleTariffQuantities[1], range[1]);

      return [min, max];
    }

    return saleTariffQuantities ?? range ?? [0, 0];
  };

  const [morningHours, lateHours] = splitHourList(hoursList);

  const updateTariff = (typeId: string, selectedQuantity: number) => {
    const tariffPerType = listOfTariffsPerType.find((t) => t.type.id === typeId);
    if (tariffPerType) {
      dispatch(updateProductAvailableDateTariff({ id: widgetId, value: { ...tariffPerType, selectedQuantity } }));
    }
  };
  const handleSelectHour = (value: ScheduleTime, error: boolean) => {
    if (error) {
      return;
    }

    track('productAdderTimeSelected', {
      productId: product.id,
      productName: product.name,
      date: selectedDate,
      time: value.formattedTime,
    });

    dispatch(changeSelectedHour({ id: widgetId, value: selectedHour?.minutes === value.minutes ? undefined : value }));
  };

  const tariffPriceInfo = (tariffPerType: TariffsPerType, useOldPrice = false) => {
    if (!tariffPerType.schedulings.length) {
      const currentType = tariffPerType.type;

      const tariff = tariffPerType.tariffGroup?.tariffs.find((tariff) => tariff.type.id === currentType.id);

      if (tariff) {
        return getFormattedTariffPrice(
          {
            priceCents: tariff.priceCents,
            oldPriceCents: tariff.oldPriceCents ?? null,
          },
          useOldPrice,
        );
      }
    }

    if (selectedHour) {
      const tariff = getTariffPriceForScheduleTime(tariffPerType, selectedHour);

      if (tariff) {
        return getFormattedTariffPrice(tariff, useOldPrice);
      }
    }

    const possiblePrices: ITariffPrice[] = [];
    for (const scheduling of tariffPerType.schedulings) {
      const tariff = getCorrectTariffFromScheduling(tariffPerType, scheduling)!;

      for (const schedulePrice of scheduling.tariffScheduleUnitPrices) {
        for (const tariffPrice of schedulePrice.tariffs) {
          if (tariffPrice.tariffId === tariff.id) {
            possiblePrices.push({
              priceCents: tariffPrice.priceCents,
              oldPriceCents: tariff.oldPriceCents ?? null,
            });
          }
        }
      }
    }

    if (possiblePrices.length === 1) {
      return getFormattedTariffPrice(possiblePrices[0]!, useOldPrice);
    }

    const priceValues = possiblePrices
      .map<number | null | undefined>((t) => (useOldPrice ? t.oldPriceCents : t.priceCents))
      .filter((v) => v != null)
      .sort((a, b) => a! - b!);

    if (!priceValues.some((value) => value != null)) {
      return '';
    }

    const minCents = priceValues[0]!;
    const maxCents = priceValues[priceValues.length - 1]!;

    if (minCents === maxCents) {
      return formatCents(maxCents);
    }

    return `${formatCents(minCents)} - ${formatCents(maxCents)}`;
  };

  const getFormattedTariffPrice = (tariffPrice: ITariffPrice, useOldPrice: boolean) => {
    if (useOldPrice) {
      return tariffPrice.oldPriceCents != null ? formatCents(tariffPrice.oldPriceCents) : '';
    }

    return (
      (tariffPrice.oldPriceCents != null ? i18n('adder_price_to') + ' ' : '') + formatCents(tariffPrice.priceCents)
    );
  };

  const tariffDescription = (tariffPerType: TariffsPerType) => {
    const minutesSelected = selectedHour ? selectedHour.minutes : null;
    const tariff = getSelectedTariff(tariffPerType, minutesSelected);

    return tariff && tariff.description ? tariff.description : '';
  };

  const isSellingModeWithDateOnly = product.sellingMode === SellingMode.WithDateOnly;

  // Check it tariffs quantity respects any full date scheduling, if present
  const schedulings = listOfTariffsPerType.reduce(
    (carry, tariffsPerType) => [...carry, ...(tariffsPerType.schedulings ?? [])],
    [] as Scheduling[],
  );

  // Can ignore general availableAmount if null (as it is only null when the quantity is unlimited)
  // and if 0 (as it is only 0 when the checkbox "limit_product_availability" is checked)
  const wholeDateScheduling = schedulings.find(
    (sch) =>
      sch.occupationRates.quantity?.availableAmount != null && sch.occupationRates.quantity?.availableAmount != 0,
  );
  const areAllQtdGreaterThanLimit = wholeDateScheduling
    ? listOfTariffsPerType.reduce((acc, value) => acc + value.selectedQuantity, 0) >
      wholeDateScheduling.occupationRates.quantity!.availableAmount!
    : false;

  // Check if the tariff type respects the tariff date limit
  const isNotPossibleToSelect = (id: string): boolean => {
    const tariffsPerType = listOfTariffsPerType.find((tariffPerType) => tariffPerType.type.id === id);
    if (tariffsPerType) {
      for (const scheduling of tariffsPerType.schedulings) {
        const tariff = scheduling.tariffGroup.tariffs.find((t) => t.type.id === id);

        if (tariff && scheduling.occupationRates.tariffQuantities) {
          const tariffOccupation = scheduling.occupationRates.tariffQuantities.find((tq) => tq.tariffId === tariff.id);

          if (
            tariffOccupation &&
            tariffOccupation.occupation.availableAmount != null &&
            tariffOccupation.occupation.availableAmount < tariffsPerType.selectedQuantity
          ) {
            return true;
          }
        }
      }
    }

    return false;
  };

  const isTariffDependent = (id: string) => {
    for (const tariffPerType of listOfTariffsPerType) {
      if (tariffPerType.type.id === id) {
        const tariff = getSelectedTariff(tariffPerType, selectedHour ? selectedHour.minutes : null);
        return tariff?.isDependent;
      }
    }

    return false;
  };

  const isDependentTariffAndIsSelected = (id: string) => {
    const tariffType = listOfTariffsPerType.find((tariffType) => id === tariffType.type.id);
    return tariffType && tariffType.selectedQuantity > 0 && isTariffDependent(id);
  };

  const showIndependentTariffNotSelectedError = (id: string) => {
    if (!tariffDepedencyStatus(listOfTariffsPerType, selectedHour).areSomeIndependentTariffSelected) {
      return isDependentTariffAndIsSelected(id);
    }
    return false;
  };

  const hasNotTariffDependencyError = !tariffDepedencyStatus(listOfTariffsPerType, selectedHour)
    .hasTariffDependencyError;

  const shouldDisplaySingleErrorLabel = (id: string) =>
    isSellingModeWithDateOnly && isNotPossibleToSelect(id) && !areAllQtdGreaterThanLimit;
  const shouldDisplayMultipleErrorLabel = isSellingModeWithDateOnly && areAllQtdGreaterThanLimit;

  const areAllTariffsHoursUnavailable =
    [...morningHours, ...lateHours].every((hour) => hour.error) &&
    listOfTariffsPerType.some((tariffType) => tariffType.selectedQuantity);

  return (
    <>
      <ProductAdderTariffContainer>
        <ProductAdderOngoingCartResume hideTariffs={true} />
        <TariffsContainer>
          {tariffsExhibitedListSorted.map((tariff) => {
            const tariffOldPrice = tariffPriceInfo(tariff, true);
            const tariffPrice = tariffPriceInfo(tariff);

            return (
              <div key={tariff.type.id}>
                <TariffInfoContainer
                  isAvailable={tariff.isAvailable}
                  hasError={areAllQtdGreaterThanLimit && isSellingModeWithDateOnly}
                >
                  <TariffAreaInfo>
                    <TariffItemContainer>
                      <TariffItemLabel>
                        <TariffItemNameLabel>{tariff.type.name}</TariffItemNameLabel>
                        {isTariffDependent(tariff.type.id) && (
                          <TariffItemIsDependentLabel>{i18n('adder_dependent_tariff')}</TariffItemIsDependentLabel>
                        )}
                      </TariffItemLabel>
                      <TariffItemValue>
                        {tariffOldPrice && !tariffPackageDiscount && (
                          <TariffOldPrice>
                            {i18n('adder_price_from')} <span>{tariffOldPrice}</span>
                          </TariffOldPrice>
                        )}

                        <TariffPrice lineThrough={!!tariffPackageDiscount}>{tariffPrice}</TariffPrice>

                        {tariffPackageDiscount && (
                          <TariffPrice>{tariffPriceWithDiscount(tariffPrice, tariffPackageDiscount)}</TariffPrice>
                        )}
                      </TariffItemValue>
                    </TariffItemContainer>
                    <TariffQuantityContainer>
                      {product && tariff.isAvailable ? (
                        <>
                          <TariffQuantityLabel>{i18n('adder_quantity')}</TariffQuantityLabel>
                          <TariffSelectInputArea>
                            <BasicQuantitySelectorInput
                              value={tariff.selectedQuantity ? tariff.selectedQuantity : 0}
                              setValue={(quantity) => updateTariff(tariff.type.id, quantity)}
                              defaultRange={[product.minSaleTariffQuantity, product.maxSaleTariffQuantity]}
                              calculatedRange={acceptableQuantities(tariff)}
                            />
                          </TariffSelectInputArea>
                        </>
                      ) : (
                        <UnavailableTariffLabel>{i18n('adder_unavailableTariffNotice')}</UnavailableTariffLabel>
                      )}
                    </TariffQuantityContainer>
                  </TariffAreaInfo>
                  <TariffDescription>{tariffDescription(tariff)}</TariffDescription>
                </TariffInfoContainer>

                {tariff.isAvailable && shouldDisplaySingleErrorLabel(tariff.type.id) ? (
                  <ProductAdderErrorLabel>{i18n('adder_singleErrorLabel')}</ProductAdderErrorLabel>
                ) : null}

                {showIndependentTariffNotSelectedError(tariff.type.id) && (
                  <ProductAdderErrorLabel>{i18n('adder_dependent_tariff_selection_error')}</ProductAdderErrorLabel>
                )}
              </div>
            );
          })}

          {shouldDisplayMultipleErrorLabel && (
            <ProductAdderErrorLabel>{i18n('adder_multipleErrorLabel')}</ProductAdderErrorLabel>
          )}
        </TariffsContainer>
        {hasNotTariffDependencyError ? (
          <TimeSelector
            morningTimes={morningHours}
            nightTimes={lateHours}
            onSelectTime={handleSelectHour}
            areAllTimesUnavailable={areAllTariffsHoursUnavailable}
            hidden={!(product?.sellingMode == SellingMode.WithDateAndTime && hoursList?.length)}
            selectedTime={selectedHour}
          />
        ) : null}

        {scheduling && <SeatsUnavailableAlert scheduling={scheduling} timeMinutes={selectedTime} />}
      </ProductAdderTariffContainer>
      <ProductAdderFooterButtons />
    </>
  );
};

const TariffsContainer = styled.div``;

interface TariffInfoContainerProps {
  hasError: boolean;
  isAvailable: boolean;
}

const ProductAdderTariffContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TariffItemContainer = styled.div``;

const TariffItemNameLabel = styled.p`
  ${BodyRegularSemiBold}
  ${DarkTextColor}
`;

const TariffItemIsDependentLabel = styled.p`
  ${BodySmallSummary}
  margin-left: 16px;
  font-style: italic;
`;

const TariffItemLabel = styled.div`
  display: flex;
  align-items: center;
`;

const TariffItemValue = styled.div`
  ${BodyRegular}
`;

const TariffOldPrice = styled.p`
  ${BodySmall}
  span {
    text-decoration: line-through;
  }
`;
const TariffPrice = styled.p<{ lineThrough?: boolean }>`
  ${BodyRegularBold}

  ${(props) => (props.lineThrough ? 'text-decoration: line-through;' : '')}
`;

const TariffQuantityContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: end;
  align-items: flex-end;
  width: auto;
  flex: 0;
  min-width: 84px;
`;

const TariffAreaInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TariffDescription = styled.p`
  ${BodySmall}
`;

const TariffQuantityLabel = styled.p`
  ${BodySmall}
  ${DarkTextColor}
`;

const TariffSelectInputArea = styled.div`
  width: 100%;
`;

const TariffInfoContainer = styled.div<TariffInfoContainerProps>`
  background-color: #f6f6f6;
  margin: 16px 0;
  padding: 16px;
  display: flex;
  flex-flow: column;
  gap: 5px 0;
  border-radius: 8px;
  ${(props) => props.hasError && 'border: 1px solid #EB3223;'};
  ${(props) => !props.isAvailable && 'opacity: 0.5;'};
`;

const UnavailableTariffLabel = styled.div`
  ${BodyRegularMedium};
  width: 100%;
  padding: 4px;
  box-sizing: border-box;
  background: #cdcdcd;
  border-radius: 4px;
  color: #5f5f5f;
  text-align: center;
`;

const ProductAdderErrorLabel = styled.p`
  ${BodySmallMedium};
  padding: 8px;
  background-color: #ffd2cd;
  border-radius: 4px;
  margin-top: 8px;
  color: #c7372c;
`;

export interface TariffsPerTypeExtended extends TariffsPerType {
  isAvailable: boolean;
}

export default ProductAdderTariff;
